import {hel_canvas_SetLayoutShow, hel_canvas_SetGUIShow} from '../logics/gui_caller';

import {
  ETextParamType_SetMicTalkingState,
  ETextParamType_OnSendTextChat,
  ETextParamType_CurrentUserName
} from './const';
import {
  setSkywayMicStateTimer,
  getSkywayMicStateTimer,
  setOnloadedProcedureList,
  getSkywayMyinfoProcedureList,
  setCurrentUserName,
  getLiveMicStateTimer,
  setLiveMicStateTimer
} from './dataOps';
import {
  getLocalTextConfigUrlDOMElement,
  getConfigUrlDOMElement,
  getLocalTextUserNameDOMElement,
  getUserNameDOMElement,
  getSkywayMsgDOMElement,
  getSkywayLocalTextDOMElement
} from './domOps';
import {getLocalStream, getLiveLocalStream} from './localStream';
import {hel_DispatchEvent, hel_setText} from './moduleCaller';

import type {ProcedureOnloadedCallFunc} from '../types/internal';

declare global {
  /* eslint-disable no-var -- NOTE: TypeScriptのグローバルスコープ拡張では「var」が必須 */
  var hel_analytics: typeof gtag | undefined;
  var dataLayer: Record<string, any>[];
  /* eslint-enable */
}

/**
 * @param name
 * @param value
 */
export const hel_set_cookie = (name: string, value: string) => {
  const expires = ';Expires=Thu, 1-Jan-2030 00:00:00 GMT';

  // Cookie needs URL-encode.
  document.cookie = name + '=' + encodeURIComponent(value) + expires;
};

/**
 * @param name
 */
export const hel_get_cookie = (name: string): string | undefined => {
  const matches = document.cookie.match(
    // eslint-disable-next-line no-useless-escape
    new RegExp('(?:^|; )' + name.replace(/([\.$?*|{}\(\)\[\]\\\/\+^])/g, '\\$1') + '=([^;]*)')
  );
  if (!matches) {
    return undefined;
  }

  if (matches[1] === undefined) throw new Error();
  return decodeURIComponent(matches[1]);
};

/**
 * 指定したMediaStreamの音量を測り、アイコンを光らせるためのタイマーを返す
 * @param localStream
 */
const createDetectVoiceTimer = (localStream: MediaStream): number => {
  const audioCtx = new AudioContext();
  const input = audioCtx.createMediaStreamSource(localStream);
  const analyzer = audioCtx.createAnalyser();
  input.connect(analyzer);
  const timeDomainArray = new Float32Array(analyzer.fftSize);
  const frequencyArray = new Float32Array(analyzer.frequencyBinCount);
  const timer = window.setInterval(
    hel_skyway_detect_voice.bind(null, input, analyzer, timeDomainArray, frequencyArray),
    200
  );
  return timer;
};

/**
 * 一般ユーザ用
 * @param state
 */
export const setMicEnabled = (state: boolean): void => {
  const timer = getSkywayMicStateTimer();
  if (timer !== undefined) {
    window.clearInterval(timer);
  }

  if (state) {
    const localStream = getLocalStream();
    if (!localStream) {
      return;
    }
    const newTimer = createDetectVoiceTimer(localStream);
    setSkywayMicStateTimer(newTimer);
  } else {
    hel_canvas_SetLayoutShow('HUD_Voice', false);
    hel_setText(ETextParamType_SetMicTalkingState, '0');
  }
};

/**
 * 発言者モードユーザ用
 * @param state
 */
export const setLiveMicEnabled = (state: boolean): void => {
  const timer = getLiveMicStateTimer();
  if (timer) {
    window.clearInterval(timer);
  }

  if (state) {
    const localStream = getLiveLocalStream();
    if (!localStream) {
      return;
    }
    const newTimer = createDetectVoiceTimer(localStream);
    setLiveMicStateTimer(newTimer);
  } else {
    hel_canvas_SetLayoutShow('HUD_Voice', false);
    hel_setText(ETextParamType_SetMicTalkingState, '0');
  }
};

export const hel_onloaded_add = (callFunc: ProcedureOnloadedCallFunc) => {
  setOnloadedProcedureList(callFunc);
};

export const hel_skyway_myinfo_bridge_call = () => {
  for (const procedure of getSkywayMyinfoProcedureList()) {
    procedure();
  }
};

export const hel_toast_notify_message = (
  message: string,
  beginDeltaTime = 250,
  stayTime = 8000,
  endDeltaTime = 250
) => {
  const JSONData = {
    message: message,
    beginDeltaTime: beginDeltaTime,
    stayTime: stayTime,
    endDeltaTime: endDeltaTime
  };

  hel_DispatchEvent('toast-notify-message', JSON.stringify(JSONData));
};

export const hel_send_notify_message = (
  noticeTypeID: number,
  message: string,
  viewTime: number,
  identifyKey: string,
  optionData: string
) => {
  const JSONData = {
    noticeTypeID: noticeTypeID,
    message: message,
    viewTime: viewTime,
    identifyKey: identifyKey,
    optionData: optionData
  };

  hel_DispatchEvent('send-notify-message', JSON.stringify(JSONData));
};

export const hel_textchat_notify_message = (param: string, isSystem: boolean) => {
  if (isSystem) {
    hel_DispatchEvent('textchat-notify-system-message', param);
  } else {
    hel_DispatchEvent('textchat-notify-chat-message', param);
  }
};

export const hel_textchat_send_message = () => {
  hel_setText(ETextParamType_OnSendTextChat, '');
};

export const hel_vcc_notify_removed = () => {
  hel_DispatchEvent('vcc-notify-removed', '');
};

/**
 * @param name
 * @param params
 */
export const hel_gtag = (...args: Gtag.GtagCommands['event']) => {
  if (typeof globalThis.hel_analytics === 'function') {
    globalThis.hel_analytics('event', ...args);
  } else if (typeof globalThis.gtag === 'function') {
    globalThis.gtag('event', ...args);
  } else if (typeof globalThis.dataLayer === 'object') {
    const analytics = (event: any, name: any, params: any) => {
      const data = {
        event: name,
        ...params
      };
      globalThis.dataLayer.push(data);
    };

    analytics('event', ...args);
  }
};

/**
 * @param show
 */
export const hel_set_show_TextField_configurl = (show: boolean) => {
  const localTextConfigUrl = getLocalTextConfigUrlDOMElement();
  if (localTextConfigUrl !== undefined) {
    localTextConfigUrl.style.visibility = show ? 'visible' : 'hidden';
  }
  const configUrl = getConfigUrlDOMElement();
  if (configUrl !== undefined) {
    configUrl.style.visibility = show ? 'visible' : 'hidden';
  }
};

/**
 * @param show
 */
export const hel_set_show_TextField_UserName = (show: boolean) => {
  const localTextUserName = getLocalTextUserNameDOMElement();
  if (localTextUserName !== undefined) {
    localTextUserName.style.visibility = show ? 'visible' : 'hidden';
  }

  const userName = getUserNameDOMElement();
  if (userName !== undefined) {
    userName.style.visibility = show ? 'visible' : 'hidden';
  }
};

/**
 * @param byCookie
 */
export const hel_post_TextField_UserName = (byCookie: boolean) => {
  let result_uname: string | undefined;

  if (byCookie) {
    const uname = hel_get_cookie('playername');
    if (uname !== undefined) {
      if (uname.length > 0) {
        result_uname = uname;
      }
    }
  } else {
    const localTextUserName = getLocalTextUserNameDOMElement();
    if (localTextUserName !== undefined) {
      result_uname = (localTextUserName as any).value;
    }
  }

  if (result_uname === undefined) {
    return;
  }

  // 文字列を『全角10文字、半角20文字』の制限に従い、切り取る
  let char_num = 0;
  const characters = Array.from(result_uname);
  for (const [i, c] of characters.entries()) {
    const incNum = /[ -~]/.test(c) ? 1 : 2;
    // 20文字を超すならここで処理を抜けて文字列を切り取る
    if (char_num + incNum > 20) {
      result_uname = characters.slice(0, i).join('');
      break;
    }

    char_num += incNum;
  }

  // console.debug("%s / %d", result_uname, char_num);

  // Send UserName to c++ side via SetText()
  setCurrentUserName(result_uname);
  hel_setText(ETextParamType_CurrentUserName, result_uname);
};

/**
 * @param show
 */
export const hel_skyway_set_show_chatbox = (show: boolean) => {
  const msg = getSkywayMsgDOMElement();
  if (msg !== undefined) {
    msg.style.visibility = show ? 'visible' : 'hidden';
  }
  const localText = getSkywayLocalTextDOMElement();
  if (localText === undefined) {
    return;
  }

  localText.style.visibility = show ? 'visible' : 'hidden';
};

const hel_skyway_detect_voice = (
  input: MediaStreamAudioSourceNode,
  analyzer: AnalyserNode,
  timeDomainArray: Float32Array,
  frequencyArray: Float32Array
) => {
  analyzer.getFloatTimeDomainData(timeDomainArray);
  analyzer.getFloatFrequencyData(frequencyArray);
  const maxDomain = Math.max(...timeDomainArray);
  // console.debug('voice!', timeDomainArray, frequencyArray);
  // console.debug('voice!', analyzer.minDecibels, analyzer.maxDecibels);
  // console.debug('voice!', maxDomain);

  const voice_threshold = 0.05;
  if (maxDomain > voice_threshold) {
    const VolumeRate = (maxDomain * 100.0).toFixed();

    hel_canvas_SetLayoutShow('HUD_Voice', true);
    hel_setText(ETextParamType_SetMicTalkingState, VolumeRate);
    hel_canvas_SetGUIShow('hud_voicechat_on', 'false', false);
  } else {
    hel_canvas_SetLayoutShow('HUD_Voice', false);
    hel_setText(ETextParamType_SetMicTalkingState, '0');
    hel_canvas_SetGUIShow('hud_voicechat_on', 'true', false);
  }
};
